import React from 'react';
import styled from 'styled-components';
import MuiMenuItem from '@mui/material/MenuItem';
import Icon from '@mui/material/Icon';
import ListItemIcon from '@mui/material/ListItemIcon';
import { Divider, SystemIcon, tokens, Typography } from '@utmost.co/utmost-design-system';
import { TypeaheadOption } from './types';

interface Props {
  recentItems: TypeaheadOption<any>[];
  itemSize: number;
  selected?: TypeaheadOption<any>[];
  multiple?: boolean;
  onItemClick: (item: TypeaheadOption<any>) => void;
  onRemoveItem: (itemId: string | number) => void;
}

export const RecentItemsList: React.FC<Props> = ({
  recentItems,
  itemSize,
  selected,
  multiple,
  onItemClick,
  onRemoveItem
}) => {
  if (!recentItems || recentItems.length === 0) {
    return null;
  }

  return (
    <div>
      <StyledSectionHeader>
        <Typography variant="label2" color={tokens.colorOnSurfaceVariant}>
          Recent Items
        </Typography>
      </StyledSectionHeader>
      {recentItems.map((item) => {
        const isSelected = selected?.map((s) => s.id)?.includes(item.id) ?? false;

        return (
          <StyledRecentMenuItem
            key={`recent-${item.id}`}
            data-test="select-menu-item-recent"
            $size={itemSize}
            $selected={isSelected}
            data-test-value={item.label}
            onMouseDown={() => onItemClick(item)}
            value={item.value as any}
          >
            {multiple && isSelected ? (
              <ListItemIcon>
                <Icon>
                  <SystemIcon name="check" />
                </Icon>
              </ListItemIcon>
            ) : item.icon ? (
              <ListItemIcon>
                <Icon>{item.icon}</Icon>
              </ListItemIcon>
            ) : null}
            <LabelContainer>
              <StyledOptionLabel className="option-label" title={item.label}>
                {item.label}
              </StyledOptionLabel>
              {(item.subtitle ?? item.code) && <StyledSecondary>{item.subtitle ?? item.code}</StyledSecondary>}
            </LabelContainer>
            <StyledRemoveButton
              aria-label="Remove recent item"
              className="remove-button"
              title="Remove from Recent Items"
              onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                onRemoveItem(item.id);
              }}
            >
              <SystemIcon name="close" />
            </StyledRemoveButton>
          </StyledRecentMenuItem>
        );
      })}
      <StyledDivider />
    </div>
  );
};

const StyledSectionHeader = styled.div`
  padding: 12px 16px;
`;

const StyledRecentMenuItem = styled(MuiMenuItem)<{ $size: number; $selected?: boolean }>`
  align-items: flex-start;
  display: flex;
  height: ${({ $size }) => $size}px;
  background: ${({ $selected }) => ($selected ? '#e1ebf5' : undefined)} !important;
  &:hover {
    background: #f0f1f2 !important;
    .remove-button { 
      opacity: 1; 
    }
    .option-label { 
      overflow-x: auto; 
    }
  }
`;

const StyledRemoveButton = styled.button`
  border-radius: 50%;
  background: transparent;
  border: none;
  cursor: pointer;
  opacity: 0;
  padding: 4px;
  transition: opacity 0.2s ease;
  width: 28px;
`;

const LabelContainer = styled.div`
  color: ${tokens.colorOnSurface};
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  overflow: hidden;
`;

const StyledOptionLabel = styled.span`
  display: block;
  font-size: 16px;
  line-height: 22px;
  white-space: nowrap;
  overflow-x: hidden; /* becomes auto on parent hover */
  overflow-y: hidden;
  scrollbar-width: thin;
`;

const StyledSecondary = styled.span`
  font-size: 12px;
  line-height: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledDivider = styled(Divider)`
  margin-bottom: 4px;
`;
